import React, { useCallback } from 'react';
import { Camera, PenLine, ImageIcon, FileSpreadsheet } from 'lucide-react';
import { Heading, MonoLabel, Body } from '@/components/rc';
import { hapticLight } from '@/utils/haptics';

interface ModeSelectorProps {
  onCameraCapture: (file: File) => void;
  onGalleryCapture: (file: File) => void;
  onManualEntry: () => void;
  onImport?: () => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB

/**
 * Holds the input for the picker that is currently open. The element is kept
 * off-DOM (an in-DOM hidden input can be auto-triggered by focus traps such as
 * Radix Dialog on Chrome Android/Desktop), so this module-level reference is
 * what keeps it — and its pending `change` listener — alive while the page sits
 * in the background with the camera app in front.
 */
let activePicker: HTMLInputElement | null = null;

/**
 * Opens a native file picker by creating a temporary <input type="file">.
 *
 * IMPORTANT: Must be called within a direct user gesture (click/tap handler)
 * for reliable behaviour on mobile browsers. Do NOT call from
 * requestAnimationFrame, setTimeout, or useEffect.
 *
 * Equally important: do not touch the History API (pushState/replaceState) or
 * navigate in the same tick as this call. Mobile WebViews drop the pending
 * file-chooser callback when the frame navigates, so the camera opens but the
 * captured photo is never delivered back to the page.
 */
export function openFilePicker(
  opts: { capture?: boolean },
  onFile: (file: File) => void,
) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  if (opts.capture) input.setAttribute('capture', 'environment');

  const release = () => { if (activePicker === input) activePicker = null; };

  input.addEventListener('change', () => {
    release();
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > MAX_FILE_SIZE) return;
    onFile(file);
  });
  // Fired when the user backs out of the picker without choosing anything.
  input.addEventListener('cancel', release);

  activePicker = input;
  input.click();
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ onCameraCapture, onGalleryCapture, onManualEntry, onImport }) => {
  const handleCamera = useCallback(() => {
    hapticLight();
    openFilePicker({ capture: true }, onCameraCapture);
  }, [onCameraCapture]);

  const handleGallery = useCallback(() => {
    hapticLight();
    openFilePicker({ capture: false }, onGalleryCapture);
  }, [onGalleryCapture]);

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-[max(4rem,env(safe-area-inset-bottom))] space-y-8">
      {/* Header */}
      <div className="text-center space-y-2">
        <Heading scale="title">Add Wine</Heading>
        <MonoLabel size="micro" colour="ghost">
          Scan, upload or enter manually
        </MonoLabel>
      </div>

      {/* Options */}
      <div className="w-full max-w-sm space-y-3">
        {/* Scan a Label — triggers camera */}
        <button
          type="button"
          onClick={handleCamera}
          className="w-full flex items-center gap-4 p-5 border-2 border-[var(--rc-ink-primary)] bg-[var(--rc-surface-primary)] hover:bg-[var(--rc-surface-secondary)] transition-colors rounded-[var(--rc-radius-md)] text-left group outline-none focus-visible:ring-2 focus-visible:ring-[var(--rc-accent-pink)] focus-visible:ring-offset-2"
        >
          <div className="w-12 h-12 rounded-full bg-[var(--rc-accent-pink)] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
            <Camera size={24} className="text-[var(--rc-ink-on-accent)]" />
          </div>
          <div>
            <Body size="body" weight="bold">SCAN A LABEL</Body>
            <MonoLabel size="micro" colour="ghost">Take a photo of the wine label</MonoLabel>
          </div>
        </button>

        {/* Choose from gallery */}
        <button
          type="button"
          onClick={handleGallery}
          className="w-full flex items-center gap-4 p-5 border border-[var(--rc-border-emphasis)] bg-[var(--rc-surface-primary)] hover:bg-[var(--rc-surface-secondary)] transition-colors rounded-[var(--rc-radius-md)] text-left group outline-none focus-visible:ring-2 focus-visible:ring-[var(--rc-accent-pink)] focus-visible:ring-offset-2"
        >
          <div className="w-12 h-12 rounded-full bg-[var(--rc-surface-secondary)] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
            <ImageIcon size={24} className="text-[var(--rc-ink-primary)]" />
          </div>
          <div>
            <Body size="body" weight="bold">CHOOSE FROM GALLERY</Body>
            <MonoLabel size="micro" colour="ghost">Select an existing photo</MonoLabel>
          </div>
        </button>

        {/* Enter Manually */}
        <button
          type="button"
          onClick={onManualEntry}
          className="w-full flex items-center gap-4 p-5 border border-[var(--rc-border-emphasis)] bg-[var(--rc-surface-primary)] hover:bg-[var(--rc-surface-secondary)] transition-colors rounded-[var(--rc-radius-md)] text-left group outline-none focus-visible:ring-2 focus-visible:ring-[var(--rc-accent-pink)] focus-visible:ring-offset-2"
        >
          <div className="w-12 h-12 rounded-full bg-[var(--rc-surface-secondary)] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
            <PenLine size={24} className="text-[var(--rc-ink-primary)]" />
          </div>
          <div>
            <Body size="body" weight="bold">ENTER MANUALLY</Body>
            <MonoLabel size="micro" colour="ghost">Type wine details by hand</MonoLabel>
          </div>
        </button>

        {/* Import Spreadsheet */}
        {onImport && (
          <button
            type="button"
            onClick={() => { hapticLight(); onImport(); }}
            className="w-full flex items-center gap-4 p-5 border border-[var(--rc-border-emphasis)] bg-[var(--rc-surface-primary)] hover:bg-[var(--rc-surface-secondary)] transition-colors rounded-[var(--rc-radius-md)] text-left group outline-none focus-visible:ring-2 focus-visible:ring-[var(--rc-accent-pink)] focus-visible:ring-offset-2"
          >
            <div className="w-12 h-12 rounded-full bg-[var(--rc-surface-secondary)] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <FileSpreadsheet size={24} className="text-[var(--rc-ink-primary)]" />
            </div>
            <div>
              <Body size="body" weight="bold">IMPORT SPREADSHEET</Body>
              <MonoLabel size="micro" colour="ghost">CSV from Vivino, CellarTracker, etc.</MonoLabel>
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default ModeSelector;
